import _ from "lodash";
import { inject, observer } from "mobx-react";
import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import MButton from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Input from "@material-ui/core/Input";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Select from "@material-ui/core/Select";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Slack from "mdi-material-ui/Slack";
import Twitter from "mdi-material-ui/Twitter";
import Youtube from "mdi-material-ui/Youtube";
import Email from "mdi-material-ui/Email";
import {
  FREQUENCY_DISPLAY_NAMES,
  PLAY_ORDERING_TYPE_DISPLAY_NAMES,
  RULE_FORM_FIELD_LABELS,
  VIDEO_FORMAT_OPTIONS,
  DEFAULT_UNSET_CROP_RATIO
} from "utils/constants";
import MultiSelect from "components/MultiSelect";

import { LabelWithIcon } from "components/LabelWithIcon";
import ForgeRuleFormItem from "components/ForgeRuleFormItem";
import WatermarkFormItem from "components/WatermarkFormItem";
import CaptionFormItem from "components/CaptionFormItem";

const styles = theme => ({
  container: {
    display: "flex",
    flexWrap: "wrap"
  },
  shrink: {
    transform: "translate(0, 1.5px) scale(0.75)",
    transformOrigin: "top left"
  },
  menu: {
    width: 200
  },
  button: {
    marginTop: "20px"
  },
  expansion: {
    width: "100%",
    margin: "20px 0"
  },
  formSection: {
    margin: "20px 0",
    width: "100%"
  },
  videoOptionError: {
    marginTop: 0,
    marginBottom: theme.spacing(1),
    "&:last-of-type": {
      marginBottom: 0
    }
  }
});

class RuleForm extends React.Component {
  constructor(props) {
    super(props);
    this._submitForm = this._submitForm.bind(this);
  }

  _submitForm() {
    const { ruleStore } = this.props;
    ruleStore.disableSaveButton();
    ruleStore
      .putRule()
      .then(() => {
        window.location = "/rules";
        ruleStore.clear();
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        ruleStore.enableSaveButton();
      });
  }

  render() {
    const { classes, sportyMetadataStore, ruleStore } = this.props;

    return (
      <form className={classes.container} noValidate autoComplete="off">
        <TextField
          id="name"
          label={RULE_FORM_FIELD_LABELS.NAME}
          InputLabelProps={{
            shrink: true
          }}
          placeholder="All Ohtani home runs"
          value={ruleStore.name}
          error={ruleStore.errors.name}
          onChange={e => ruleStore.setName(e.target.value)}
          onBlur={() => ruleStore.validateName()}
          fullWidth
        />
        {ruleStore.errors.name ? (
          <FormHelperText error>Rule name cannot be empty.</FormHelperText>
        ) : null}
        <FormControl component="fieldset" margin="normal" fullWidth>
          <FormLabel className={classes.shrink} data-shrink={true}>
            Enabled
          </FormLabel>
          <FormControlLabel
            control={
              <Switch
                checked={ruleStore.enabled}
                onChange={(e, checked) => ruleStore.setEnabled(checked)}
              />
            }
          />
        </FormControl>
        <FormControl component="fieldset" margin="normal" fullWidth>
          <FormLabel className={classes.shrink} data-shrink={true}>
            {RULE_FORM_FIELD_LABELS.FREQUENCY}
          </FormLabel>
          <RadioGroup
            aria-label="frequency"
            name="frequency"
            value={ruleStore.frequency}
            onChange={e => ruleStore.setFrequency(e.target.value)}
            row={true}
          >
            <FormControlLabel
              value="PLAY_END"
              control={<Radio />}
              label={FREQUENCY_DISPLAY_NAMES["PLAY_END"]}
            />
            <FormControlLabel
              value="INNING_END"
              control={<Radio />}
              label={FREQUENCY_DISPLAY_NAMES["INNING_END"]}
            />
            <FormControlLabel
              value="HALF_INNING_END"
              control={<Radio />}
              label={FREQUENCY_DISPLAY_NAMES["HALF_INNING_END"]}
            />
            <FormControlLabel
              value="GAME_END"
              control={<Radio />}
              label={FREQUENCY_DISPLAY_NAMES["GAME_END"]}
            />
            <FormControlLabel
              value="ALL_GAMES_END"
              control={<Radio />}
              label={FREQUENCY_DISPLAY_NAMES["ALL_GAMES_END"]}
            />
          </RadioGroup>
        </FormControl>
        {_.includes(["INNING_END", "HALF_INNING_END"], ruleStore.frequency) ? (
          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel className={classes.shrink} data-shrink={true}>
              {RULE_FORM_FIELD_LABELS.CATCHUP_STYLE}
            </FormLabel>
            <RadioGroup
              aria-label="catchup-style"
              name="catchup-style"
              value={ruleStore.catchupStyle}
              onChange={e => (ruleStore.catchupStyle = e.target.value)}
              row={true}
            >
              <FormControlLabel
                value="INNING_ONLY"
                control={<Radio />}
                label="Query results will be limited to plays from the inning"
              />
              <FormControlLabel
                value="FULL_GAME"
                control={<Radio />}
                label="Query results will include all plays from the game"
              />
            </RadioGroup>
          </FormControl>
        ) : null}
        <FormControl
          component="fieldset"
          margin="normal"
          fullWidth
          error={ruleStore.errors.query}
        >
          <InputLabel htmlFor="query" shrink>
            {RULE_FORM_FIELD_LABELS.QUERY}
          </InputLabel>
          <Input
            id="query"
            value={ruleStore.query}
            multiline
            minRows={3}
            maxRows={5}
            onBlur={() => ruleStore.validateQuery()}
            placeholder="BatterName = 'Ohtani' AND ExitVelocity > 100"
            onChange={e => ruleStore.setQuery(e.target.value)}
          />
          {ruleStore.errors.query ? (
            <FormHelperText id="query-error-text">
              Your query is not valid.
            </FormHelperText>
          ) : null}
        </FormControl>
        <div className={classes.formSection}>
          <Grid container spacing={10}>
            <Grid item sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="feed-preference">
                  {RULE_FORM_FIELD_LABELS.FEED_PREFERENCE}
                </InputLabel>
                <Select
                  value={ruleStore.videoOptions.feedPreference}
                  onChange={e =>
                    ruleStore.setVideoFeedPreference(e.target.value)
                  }
                >
                  <MenuItem value="HOME">Home</MenuItem>
                  <MenuItem value="AWAY">Away</MenuItem>
                  <MenuItem value="BATTING_TEAM">Batting Team</MenuItem>
                  <MenuItem value="PITCHING_TEAM">Pitching Team</MenuItem>
                  <MenuItem value="NETWORK">Network</MenuItem>
                  <MenuItem value="WORLD">World</MenuItem>
                  <MenuItem value="THREE_D_VIZ">3D Viz</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sm={4}>
              <FormControl component="fieldset" margin="none" fullWidth>
                <FormLabel className={classes.shrink} data-shrink={true}>
                  Always Exclude Network Feed
                </FormLabel>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ruleStore.excludeNetworkFeed}
                      onChange={(e, checked) =>
                        ruleStore.setNetworkFeedExclusion(checked)
                      }
                    />
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formSection}>
          <Grid container spacing={10}>
            <Grid item sm={4}>
              <FormControl fullWidth>
                <MultiSelect
                  id={"multi-select-video-formats"}
                  label={RULE_FORM_FIELD_LABELS.FORMAT}
                  values={ruleStore.videoTypesWithLabels}
                  onChange={types => ruleStore.setVideoTypes(types)}
                  options={VIDEO_FORMAT_OPTIONS}
                />
              </FormControl>
            </Grid>
            <Grid item sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="play-order">
                  {RULE_FORM_FIELD_LABELS.ORDERING_TYPE}
                </InputLabel>
                <Select
                  value={ruleStore.videoOptions.orderingType}
                  onChange={e => ruleStore.setPlayOrderingType(e.target.value)}
                >
                  <MenuItem value="NONE">
                    {PLAY_ORDERING_TYPE_DISPLAY_NAMES["NONE"]}
                  </MenuItem>
                  <MenuItem value="CHRONO">
                    {PLAY_ORDERING_TYPE_DISPLAY_NAMES["CHRONO"]}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sm={4}>
              <WatermarkFormItem
                value={
                  ruleStore.videoOptions.watermark &&
                  ruleStore.videoOptions.watermark.filename
                }
                handleOnChange={value => ruleStore.setVideoWatermark(value)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={10}>
            <Grid item sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="limit-by">
                  {RULE_FORM_FIELD_LABELS.LIMIT_BY}
                </InputLabel>
                <Select
                  value={ruleStore.videoOptions.limitOption}
                  onChange={e => ruleStore.setVideoLimitOption(e.target.value)}
                >
                  <MenuItem value="NUM_PLAYS">Number of plays</MenuItem>
                  <MenuItem value="TIME">Time</MenuItem>
                  <MenuItem value="NONE">None</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sm={4}>
              <TextField
                id="limit-count"
                error={ruleStore.errors.limitCount}
                disabled={ruleStore.isLimitCountDisabled}
                label={`${RULE_FORM_FIELD_LABELS.LIMIT_COUNT} ${
                  ruleStore.isLimitCountDisabled
                    ? ""
                    : ruleStore.videoOptions.limitOption === "NUM_PLAYS"
                    ? "(plays)"
                    : "(minutes)"
                }`}
                InputLabelProps={{
                  shrink: true
                }}
                value={ruleStore.videoOptions.limitCount || ""}
                onBlur={() => ruleStore.validateLimitCount()}
                onChange={e => ruleStore.setVideoLimitCount(e.target.value)}
                fullWidth
              />
              {ruleStore.errors.limitCount ? (
                <FormHelperText error={ruleStore.errors.limitCount}>
                  Limit count must be > 0
                </FormHelperText>
              ) : null}
            </Grid>
            <Grid item sm={4}>
              <CaptionFormItem
                value={
                  ruleStore.videoOptions.captions &&
                  ruleStore.videoOptions.captions.language
                }
                handleOnChange={value => ruleStore.setVideoCaptions(value)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={10}>
            <Grid item sm={8}>
              {ruleStore.showVideoCreationOptions ? (
                <FormControl
                  component="fieldset"
                  margin="none"
                  fullWidth
                  error={ruleStore.errors.videoCreationOptions}
                >
                  <MultiSelect
                    id={"multi-select-video-effects"}
                    label={RULE_FORM_FIELD_LABELS.VIDEO_OPTIONS}
                    values={ruleStore.videoOptions.creationOptions.slice()}
                    options={Object.entries(
                      sportyMetadataStore.videoCreationOptions
                    ).map(([value, label]) => ({ value, label }))}
                    onChange={selectedItems =>
                      ruleStore.setVideoCreationOptions(selectedItems)
                    }
                  />
                  {ruleStore.errors.videoCreationOptions ? (
                    <FormHelperText
                      component={"div"}
                      error={ruleStore.errors.videoCreationOptions}
                    >
                      {ruleStore.videoCreationOptionsErrorMsg.map(
                        (errorMsg, i, self) => (
                          <p key={i} className={classes.videoOptionError}>
                            {errorMsg}
                          </p>
                        )
                      )}
                    </FormHelperText>
                  ) : null}
                </FormControl>
              ) : null}
            </Grid>
            <Grid item sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="crop-ratio">
                  {RULE_FORM_FIELD_LABELS.CROP_RATIO}
                </InputLabel>
                <Select
                  value={
                    (ruleStore.videoOptions.cropRatio &&
                      ruleStore.videoOptions.cropRatio.value) ||
                    DEFAULT_UNSET_CROP_RATIO
                  }
                  onChange={e => ruleStore.setVideoCropRatio(e.target.value)}
                >
                  {ruleStore.cropRatios.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formSection}>
          <Grid container>
            <Grid item sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="video-workflow">Workflow</InputLabel>
                <Select
                  value={ruleStore.videoOptions.workflow}
                  onChange={e => ruleStore.setWorkflow(e.target.value)}
                >
                  {_.map(sportyMetadataStore.workflowDetails, (value, key) => (
                    <MenuItem key={key} value={key}>
                      {_.startCase(_.toLower(key))}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{ruleStore.workflowHelperText}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </div>
        <div className={classes.expansion}>
          <Accordion expanded={ruleStore.notifications.slack.enabled}>
            <AccordionSummary>
              <FormControlLabel
                value="SLACK"
                control={
                  <Switch
                    checked={ruleStore.notifications.slack.enabled}
                    onChange={(e, checked) => {
                      ruleStore.setNotificationEnabled("slack", checked);
                    }}
                  />
                }
                label={<LabelWithIcon icon={<Slack />} label="Slack" />}
              />
            </AccordionSummary>
            <AccordionDetails style={{ display: "block" }}>
              <TextField
                id="slack-channel"
                label="Channel or Username"
                InputLabelProps={{
                  shrink: true
                }}
                placeholder="#sportybot-produced-clips"
                value={ruleStore.notifications.slack.destination}
                onChange={e =>
                  ruleStore.setNotificationDestination("slack", e.target.value)
                }
                fullWidth
                margin="normal"
              />
              <TextField
                id="slack-message"
                label="Message Template"
                InputLabelProps={{
                  shrink: true
                }}
                placeholder={`Exit Velocity: \${ExitVelocity}`}
                value={ruleStore.notifications.slack.message}
                onChange={e =>
                  ruleStore.setNotificationMessage("slack", e.target.value)
                }
                fullWidth
                multiline
                margin="normal"
              />
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={ruleStore.notifications.email.enabled}>
            <AccordionSummary>
              <FormControlLabel
                value="Email"
                control={
                  <Switch
                    checked={ruleStore.notifications.email.enabled}
                    onChange={(e, checked) =>
                      ruleStore.setNotificationEnabled("email", checked)
                    }
                  />
                }
                label={<LabelWithIcon icon={<Email />} label="Email" />}
              />
            </AccordionSummary>
            <AccordionDetails style={{ display: "block" }}>
              <TextField
                id="email-destination"
                label="Email Addresses"
                InputLabelProps={{
                  shrink: true
                }}
                placeholder="joe.davey@mlb.com, marc.squire@mlb.com"
                value={ruleStore.notifications.email.destination}
                onChange={e =>
                  ruleStore.setNotificationDestination("email", e.target.value)
                }
                fullWidth
                margin="normal"
              />
              <TextField
                id="email-message"
                label="Message Template"
                InputLabelProps={{
                  shrink: true
                }}
                placeholder={`Exit Velocity: \${ExitVelocity}`}
                value={ruleStore.notifications.email.message}
                onChange={e =>
                  ruleStore.setNotificationMessage("email", e.target.value)
                }
                fullWidth
                multiline
                margin="normal"
              />
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={ruleStore.notifications.twitter.enabled}>
            <AccordionSummary>
              <FormControlLabel
                value="Twitter"
                control={
                  <Switch
                    checked={ruleStore.notifications.twitter.enabled}
                    onChange={(e, checked) =>
                      ruleStore.setNotificationEnabled("twitter", checked)
                    }
                  />
                }
                label={<LabelWithIcon icon={<Twitter />} label="Twitter" />}
              />
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                id="tweet"
                label="Tweet"
                InputLabelProps={{
                  shrink: true
                }}
                placeholder="{{batter}} home runs on {{date}}"
                fullWidth
                multiline
                value={ruleStore.notifications.twitter.destination}
                onChange={e => {
                  ruleStore.setNotificationDestination(
                    "twitter",
                    e.target.value
                  );
                }}
                minRows={3}
                maxRows={5}
                margin="normal"
              />
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={ruleStore.notifications.youtube.enabled}>
            <AccordionSummary>
              <FormControlLabel
                value="YouTube"
                control={
                  <Switch
                    checked={ruleStore.notifications.youtube.enabled}
                    onChange={(e, checked) =>
                      ruleStore.setNotificationEnabled("youtube", checked)
                    }
                  />
                }
                label={<LabelWithIcon icon={<Youtube />} label="YouTube" />}
              />
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                id="youtube_title"
                label="Title"
                InputLabelProps={{
                  shrink: true
                }}
                placeholder="Top home runs on {{date}}"
                fullWidth
                multiline
                value={ruleStore.notifications.youtube.destination}
                onChange={e =>
                  ruleStore.setNotificationDestination(
                    "youtube",
                    e.target.value
                  )
                }
                minRows={3}
                maxRows={5}
                margin="normal"
              />
            </AccordionDetails>
          </Accordion>
          <ForgeRuleFormItem
            type="deltatre"
            label="Forge"
            errorField={ruleStore.errors.deltatreAutoPublish}
          />
          <ForgeRuleFormItem
            type="deltatre_milb"
            label="Forge MiLB"
            errorField={ruleStore.errors.milbDeltatreAutoPublish}
          />
        </div>
        <MButton
          variant="contained"
          color="primary"
          disabled={ruleStore.saveButtonDisabled}
          className={classes.button}
          onClick={this._submitForm}
        >
          Save
        </MButton>
      </form>
    );
  }
}

RuleForm.propTypes = {
  classes: PropTypes.object.isRequired
};

export default inject(
  "ruleStore",
  "sportyMetadataStore",
  "watermarkStore"
)(withStyles(styles)(observer(RuleForm)));



// WEBPACK FOOTER //
// ./src/sections/RuleForm.js